import { useEffect, useRef, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { TESTIMONIALS } from '../data';
import SectionHeader from './SectionHeader';

function StarRating({ rating }) {
  return (
    <div style={{ display: 'flex', gap: '3px', marginBottom: '1rem' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} className="star">
          {n <= rating ? '★' : '☆'}
        </span>
      ))}
    </div>
  );
}

function Avatar({ initials }) {
  return (
    <div style={{
      width: '44px', height: '44px', borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--accent), #E8B4B8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: '0.8rem',
      fontFamily: 'var(--font-sans)', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Swipe logic
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      goTo((active + 1) % TESTIMONIALS.length);
    }
    if (isRightSwipe) {
      goTo(active === 0 ? TESTIMONIALS.length - 1 : active - 1);
    }
  };

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      goTo((active + 1) % TESTIMONIALS.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [active]);

  const goTo = (idx) => {
    if (idx === active || isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActive(idx);
      setIsAnimating(false);
    }, 280);
  };

  const current = TESTIMONIALS[active];

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      style={{ padding: '9rem 0', backgroundColor: 'var(--bg-alt)', overflow: 'hidden', position: 'relative' }}
    >
      {/* Decorative blobs */}
      <div style={{
        position: 'absolute', width: '500px', height: '500px',
        background: 'radial-gradient(ellipse, rgba(183,110,121,0.08) 0%, transparent 70%)',
        top: '-100px', right: '-80px', borderRadius: '50%', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: '300px', height: '300px',
        background: 'radial-gradient(ellipse, rgba(232,180,184,0.08) 0%, transparent 70%)',
        bottom: 0, left: '-50px', borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <SectionHeader
          tag="Customer Love"
          title={<>What Our <em style={{ fontStyle: 'italic', fontWeight: 300 }}>Clients Say</em></>}
          desc="Stories from our community of fashion-forward South Asian women across Italy."
          id="testimonials-heading"
        />

        {/* Main testimonial card */}
        <div
          style={{
            maxWidth: '760px', margin: '4rem auto 0',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}
        >
          <div
            className="glass-card"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEndHandler}
            style={{
              padding: 'clamp(2rem,5vw,3.5rem)',
              opacity: isAnimating ? 0 : 1,
              transform: isAnimating ? 'scale(0.97) translateY(10px)' : 'scale(1) translateY(0)',
              transition: 'opacity 0.28s ease, transform 0.28s ease',
            }}
          >
            {/* Quote icon */}
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(183,110,121,0.15), rgba(232,180,184,0.08))',
              border: '1px solid rgba(183,110,121,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '1.75rem', color: 'var(--accent)',
            }}>
              <Quote size={20} />
            </div>

            <StarRating rating={current.rating} />

            <p 
              aria-live="polite" 
              aria-atomic="true"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(1.1rem, 2.2vw, 1.45rem)',
                fontWeight: 400, fontStyle: 'italic',
                lineHeight: 1.65, color: 'var(--text)',
                marginBottom: '2rem',
              }}
            >
              "{current.text}"
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Avatar initials={current.avatar} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>
                  {current.name}
                </div>
                <div className="heading-sub" style={{ fontSize: '0.65rem', marginTop: '0.2rem' }}>
                  {current.location}
                </div>
              </div>
            </div>
          </div>

          {/* Dot navigation */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', marginTop: '2rem' }}>
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                style={{
                  width: i === active ? '28px' : '8px',
                  height: '8px',
                  borderRadius: '50px',
                  background: i === active
                    ? 'linear-gradient(135deg, var(--accent), #E8B4B8)'
                    : 'var(--border)',
                  border: 'none', cursor: 'pointer',
                  transition: 'all 0.35s cubic-bezier(0.25,1,0.5,1)',
                  margin: '0 4px',
                  padding: '12px 0', /* Increase touch target area invisibly */
                  backgroundClip: 'content-box',
                }}
              />
            ))}
          </div>
        </div>

        {/* Side previews */}
        <div className="hidden md:flex" style={{
          gap: '1rem', justifyContent: 'center',
          marginTop: '2.5rem', flexWrap: 'wrap',
        }}>
          {TESTIMONIALS.map((t, i) => {
            if (i === active) return null;
            return (
              <button
                key={t.id}
                onClick={() => goTo(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '50px',
                  cursor: 'pointer', color: 'var(--text)',
                  fontSize: '0.8rem', fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.3s ease',
                  opacity: 0.65,
                }}
                onMouseOver={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onMouseOut={e  => { e.currentTarget.style.opacity = '0.65'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <Avatar initials={t.avatar} />
                {t.name}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
